import { estimateMatchCost } from './cost'
import {
  logAiCost,
  logAiFailure,
  logAiRawResponse,
  logAiRequest,
  logAiResult,
} from './matchingLog'
import { AiMatchResponse, FieldCandidate, MatchResult } from './types'
import { createGenAiClient } from '@/utils/genAiClient'

/**
 * AI-first CSV column matcher (CRM-1175).
 *
 * Sends the first ~20 raw (unparsed) lines of the file to the GenAI gateway and
 * asks it to detect the delimiter, the header row, and a field mapping per
 * column. Any failure — network, non-2xx, malformed JSON, hallucinated field
 * key — resolves to null so the caller can fall through to the fuzzy layer.
 * The import is never blocked by this call.
 */

// The finalists that survived the CRM-1175 evals: the three Anthropic aliases
// plus DeepSeek v3.2. All produce valid JSON, correct header-row detection, and
// deterministic output on the enriched (enum-option) prompt. `aws/deepseek.v3-v1:0`
// was dropped after the enrichment: it is the only finalist that emits invalid
// JSON and misdetects the header row on the longer prompt (see the READMEs). The
// engines rejected in the first eval (both Mistrals, Pixtral, GLM-flash, MiniMax,
// Nova) failed header detection, emitted bad JSON, or the gateway refused them.
export type MatchingModelId =
  | 'jr-auto/fast' // Haiku 4.5
  | 'jr-auto/standard' // Sonnet 4.6
  | 'jr-auto/powerful' // Opus 4.6
  | 'aws/deepseek.v3.2'

// Chosen default: Haiku — within 1–3 points of the top scorer on both entities,
// never fabricates (Sonnet's one remaining flaw), cheapest/fastest Anthropic tier.
// Swap this to one of the other MatchingModelId aliases, then reload, to compare.
const MATCHING_MODEL: MatchingModelId = 'jr-auto/fast'

// Models that 400 on `temperature` (deprecated for them). Every other model
// gets `temperature: 0` for deterministic extraction — DeepSeek in particular
// needs it. Opus 4.6 is the only one that rejects it so far.
const MODELS_REJECTING_TEMPERATURE = new Set<MatchingModelId>([
  'jr-auto/powerful',
])

// The only delimiters papaparse is asked to honour. A model that returns
// anything else (a multi-char string, an injected value) falls back to comma
// rather than mis-parsing the whole file on an unexpected separator.
const VALID_DELIMITERS = new Set([',', ';', '\t', '|'])

const SYSTEM_PROMPT = `You are a CRM field mapping assistant.
Rules:
- fieldKey must be exactly one of the provided CRM field keys, or null if no match.
- Never invent field keys.
- Never map two different columns to the same fieldKey. If two columns seem to fit
  one field, keep the better match and set the other to null (or a distinct field).
- Each CRM field is annotated with its type and, for enum fields, its allowed option
  values: "key → Label [enum: opt1, opt2, …]". When several fields could fit a column
  (e.g. group vs category vs status), pick the one whose listed options actually contain
  that column's sample VALUES. If no field's options match the values, prefer null over
  guessing between near-synonym labels.
- Respect field types: only map a column to a [date] field when its values look like
  dates, to a [reference] field when its values name another record, and to a [number]
  field when its values are numeric.
- delimiter: the single character separating columns.
- headerRowIndex: 0-based index of the row containing the column HEADERS (field
  names like "Name", "Email"), NOT data. Export files often begin with junk lines
  — a title, an export date, a filter description, or blank lines — BEFORE the
  header row. Skip those: find the first row whose cells are field labels, not
  values, and report its index. If the first row is already headers, use 0.
- mappings.rawIndex is the 0-based column position within the header row.
- mappings.modelConfidence: your confidence in THIS single mapping — "high", "medium",
  or "low". Use "low" when you are guessing between plausible fields or unsure a column
  maps at all. For a null fieldKey, report how sure you are it maps to nothing.`

/** "key → Label [type: opt1, opt2, …]" — the minimized schema slice per field. */
function describeCandidate(f: FieldCandidate) {
  let line = `${f.key} → ${f.label}`
  if (f.options?.length) {
    // "label (value)" per option so the model treats a code and its localized
    // title as one option; collapse to a single token when they are equal.
    const opts = f.options
      .map((o) =>
        o.label && o.label !== o.value ? `${o.label} (${o.value})` : o.value,
      )
      .join(', ')
    line += ` [${f.type ?? 'enum'}: ${opts}]`
  } else if (f.type && f.type !== 'string') {
    line += ` [${f.type}]`
  }
  return line
}

function buildUserPrompt(
  rawLines: string[],
  fieldCandidates: FieldCandidate[],
) {
  return `Available CRM fields:
${fieldCandidates.map(describeCandidate).join('\n')}

First lines of the CSV file:
${rawLines.join('\n')}

Respond with JSON only:
{
  "delimiter": ",",
  "headerRowIndex": 0,
  "mappings": [
    { "rawIndex": 0, "fieldKey": "name", "modelConfidence": "high" },
    { "rawIndex": 1, "fieldKey": null, "modelConfidence": "high" }
  ]
}`
}

/** Remove a leading/trailing markdown code fence from a model response. */
function stripCodeFences(raw: string) {
  return raw
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '')
    .trim()
}

/** Isolate the first balanced {...} block — models sometimes wrap it in prose. */
function extractFirstObject(s: string) {
  const start = s.indexOf('{')
  if (start === -1) {
    return s
  }
  let depth = 0
  for (let i = start; i < s.length; i++) {
    if (s[i] === '{') {
      depth++
    } else if (s[i] === '}') {
      depth--
      if (depth === 0) {
        return s.slice(start, i + 1)
      }
    }
  }
  return s
}

/**
 * Discard any mapping whose fieldKey is not a known candidate. The AI output is
 * never trusted directly: a hallucinated or renamed key becomes null rather
 * than being written to columnMappings.
 */
function sanitizeMappings(raw: unknown, validKeys: Set<string>) {
  if (!Array.isArray(raw)) {
    return []
  }
  const results: MatchResult[] = []
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) {
      continue
    }
    // Narrowing an unknown object; every field is re-checked below before use.
    const { rawIndex, fieldKey, modelConfidence } = entry as {
      rawIndex?: unknown
      fieldKey?: unknown
      modelConfidence?: unknown
    }
    // A column index the model returns must be a non-negative integer. A
    // negative or fractional value would produce an orphaned mapping that
    // never lines up with a rendered column.
    if (
      typeof rawIndex !== 'number' ||
      !Number.isInteger(rawIndex) ||
      rawIndex < 0
    ) {
      continue
    }
    const validKey =
      typeof fieldKey === 'string' && validKeys.has(fieldKey) ? fieldKey : null
    results.push({
      rawIndex,
      fieldKey: validKey,
      confidence: 'ai',
      ...(modelConfidence === 'high' ||
      modelConfidence === 'medium' ||
      modelConfidence === 'low'
        ? { modelConfidence }
        : {}),
    })
  }
  return results
}

export async function matchColumnsAI(
  rawLines: string[],
  fieldCandidates: FieldCandidate[],
): Promise<AiMatchResponse | null> {
  try {
    logAiRequest({
      model: MATCHING_MODEL,
      fileLines: rawLines.length,
      candidateCount: fieldCandidates.length,
    })
    const startedAt = Date.now()
    const client = createGenAiClient()
    const stream = await client.chat.completions.create({
      model: MATCHING_MODEL,
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: buildUserPrompt(rawLines, fieldCandidates) },
      ],
      stream: true,
      // Ask the gateway to emit a final usage chunk so we can estimate cost.
      stream_options: { include_usage: true },
      // temperature: 0 for deterministic extraction, except for models that 400 on it.
      ...(MODELS_REJECTING_TEMPERATURE.has(MATCHING_MODEL)
        ? {}
        : { temperature: 0 }),
    })

    let text = ''
    let inputCredits = 0
    let outputCredits = 0
    for await (const chunk of stream) {
      text += chunk.choices[0]?.delta?.content ?? ''
      // The usage chunk arrives last and carries no choices. The gateway reports
      // credits (not raw tokens) via the prompt/completion token fields.
      if (chunk.usage) {
        inputCredits = chunk.usage.prompt_tokens ?? 0
        outputCredits = chunk.usage.completion_tokens ?? 0
      }
    }

    // Models routinely wrap JSON in ```json fences or add prose around it, so a
    // raw JSON.parse throws. Strip fences and isolate the first {...} block —
    // the same treatment the AiChatBot guard applies to model output.
    const cleaned = extractFirstObject(stripCodeFences(text))
    logAiRawResponse(text)
    const cost = estimateMatchCost({
      model: MATCHING_MODEL,
      inputCredits,
      outputCredits,
    })
    logAiCost(cost)

    // JSON.parse returns unknown; each field is narrowed before use below.
    const parsed = JSON.parse(cleaned) as {
      delimiter?: unknown
      headerRowIndex?: unknown
      mappings?: unknown
    }

    const validKeys = new Set(fieldCandidates.map((f) => f.key))
    const result = {
      delimiter:
        typeof parsed.delimiter === 'string' &&
        VALID_DELIMITERS.has(parsed.delimiter)
          ? parsed.delimiter
          : ',',
      headerRowIndex:
        typeof parsed.headerRowIndex === 'number' ? parsed.headerRowIndex : 0,
      mappings: sanitizeMappings(parsed.mappings, validKeys),
    }
    logAiResult({
      delimiter: result.delimiter,
      headerRowIndex: result.headerRowIndex,
      matched: result.mappings.filter((m) => m.fieldKey).length,
      total: result.mappings.length,
      elapsedMs: Date.now() - startedAt,
    })
    return result
  } catch (error) {
    // Any failure (network, non-2xx, bad JSON) → fuzzy fallback. Never block
    // the import. Log so the failure is diagnosable rather than silent.
    logAiFailure(error)
    return null
  }
}
